import { motion } from 'framer-motion';
import moment from 'moment';
import { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FaBirthdayCake, FaLinkedin, FaUserAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { IoCall } from 'react-icons/io5';
import { MdEmail } from 'react-icons/md';
import { applicationAnimVariants } from '../../constants/animationVariants';
import { ApplicationStatus } from '../../types/applications';
import { showFullName, showPicture } from '../../utils/commonFunctions';
import AssociateRequestExperience from '../AssociateRequestsComps/AssociateRequestExperience';
import AssociateRequestInfo from '../AssociateRequestsComps/AssociateRequestInfo';
import AcademiaSectionWrapper from '../CommonComps/AcademiaSectionWrapper';
import SkillBadge from '../CommonComps/Badges/SkillBadge';
import SectionHeader from '../CommonComps/Titles/SectionHeader';

const ApplicationPreview = ({
  applicationStatus,
}: {
  applicationStatus: ApplicationStatus;
}) => {
  // destructuring application status component here
  const { user, user_profile, user_expertise } = applicationStatus || {};

  const userExpertiseNameList = useMemo(() => {
    if (!user_expertise) return [];

    return user_expertise.map((expertise) => expertise.service_name);
  }, [user_expertise]);

  // calculating applicant's age here
  const today = moment();
  const DOB = moment(user_profile?.date_of_birth);

  const age = today.diff(DOB, 'years');

  const splittedLinkedinUrl = user_profile?.linkedin_profile.split('/');

  // rendering application preview section component here
  return (
    <motion.section
      variants={applicationAnimVariants}
      initial='initial'
      animate='animate'
    >
      <SectionHeader headerText='Application Preview' />

      <div className='lg:w-4/5 mx-auto bg-base-white rounded-xl shadow-academia-sm p-3 md:p-6 space-y-3'>
        <div>
          <div className='flex flex-col md:flex-row items-center md:items-start justify-between'>
            <div>
              <h3 className='text-2xl font-medium text-base-dark-blue'>
                {showFullName(
                  user.first_name,
                  user.middle_name,
                  user.last_name
                )}
              </h3>
              <p className='font-semibold text-center md:text-left text-base-cyan italic'>
                @{user.username}
              </p>
            </div>

            <p className='md:text-xl text-base-cyan text-center md:text-right'>
              Age: <span className='font-semibold'>{age || 'N/A'}</span>
            </p>
          </div>

          <div className='flex flex-col-reverse md:flex-row items-center justify-between mt-3 gap-3'>
            <div className='flex flex-col justify-center gap-1 bg-gray-100 rounded-lg px-4 py-2 min-h-[150px] flex-1'>
              <AssociateRequestInfo
                icon={
                  <ReactCountryFlag
                    style={{ width: '1.5em', height: '1.42em' }}
                    countryCode={user_profile ? user_profile.iso_code : ''}
                    svg
                  />
                }
                details={
                  <span className='font-semibold text-legal-dark-green'>
                    {user_profile ? user_profile.country : ''}
                  </span>
                }
              />

              {user_profile ? (
                <AssociateRequestInfo
                  icon={<FaLocationDot className='size-5' />}
                  details={
                    <>
                      From{' '}
                      <span className='font-semibold text-legal-dark-green'>
                        {user_profile.address}, {user_profile.city_name},{' '}
                        {user_profile.province_name}, {user_profile.postal_code}
                      </span>
                    </>
                  }
                />
              ) : null}

              <AssociateRequestInfo
                icon={<MdEmail className='size-5' />}
                details={
                  <span className='font-semibold text-base-orange'>
                    {user.email}
                  </span>
                }
              />

              {user_profile ? (
                <AssociateRequestInfo
                  icon={<IoCall className='size-5' />}
                  details={
                    <span className='font-semibold text-legal-dark-green'>
                      {`${user_profile.isd_code}${user_profile.phone}`}
                    </span>
                  }
                />
              ) : null}

              {user_profile?.date_of_birth ? (
                <AssociateRequestInfo
                  icon={<FaBirthdayCake className='size-5' />}
                  details={
                    <span className='font-semibold text-legal-dark-green'>
                      {moment(user_profile.date_of_birth).format(
                        'Do MMMM, YYYY'
                      )}
                    </span>
                  }
                />
              ) : null}

              {user_profile?.linkedin_profile ? (
                <AssociateRequestInfo
                  icon={<FaLinkedin className='size-5' />}
                  details={
                    <a
                      href={user_profile.linkedin_profile}
                      className='font-semibold underline text-base-orange hover:opacity-70 hover:underline duration-300'
                      target='_blank'
                      rel='noreferrer'
                    >
                      {`/in/${
                        splittedLinkedinUrl
                          ? splittedLinkedinUrl[
                              Number(splittedLinkedinUrl.length) - 1
                            ]
                            ? splittedLinkedinUrl[
                                Number(splittedLinkedinUrl.length) - 1
                              ]
                            : splittedLinkedinUrl[
                                Number(splittedLinkedinUrl.length) - 2
                              ]
                          : null
                      }`}
                    </a>
                  }
                />
              ) : null}
            </div>

            <div>
              {user.profile_picture ? (
                <div className='size-[150px] rounded-lg overflow-hidden'>
                  <img
                    className='w-full h-full object-cover'
                    src={showPicture(user.profile_picture)}
                    alt={`avatar of ${user.username}`}
                  />
                </div>
              ) : (
                <div className='flex items-center justify-center bg-gray-100 size-[150px] rounded-lg'>
                  <FaUserAlt className='size-20 text-gray-300' />
                </div>
              )}
            </div>
          </div>

          <AssociateRequestExperience
            fieldOfExpertise={userExpertiseNameList.join(', ')}
            perHourRate={user_profile?.per_hour_rate || 0}
            yearsOfExperience={user_profile?.years_of_experience || 0}
            businessRegNo={user_profile?.business_reg_no || 'N/A'}
            hstNumber={user_profile?.hst_no || 'N/A'}
            licenseNumber={user_profile?.license_number || 'N/A'}
          />

          <AcademiaSectionWrapper sectionTitle={`Applicant's Bio`}>
            <p className='text-base-dark-blue'>{user_profile?.bio || 'N/A'}</p>
          </AcademiaSectionWrapper>

          <AcademiaSectionWrapper sectionTitle={`Language expertise`}>
            <div className='flex flex-wrap gap-2'>
              {user_profile?.languages_spoken.map((lang) => (
                <SkillBadge
                  key={lang.id}
                  badgeLabel={lang.name}
                  color='#388229'
                />
              ))}
            </div>
          </AcademiaSectionWrapper>
        </div>
      </div>
    </motion.section>
  );
};

export default ApplicationPreview;
